import CHighlightedText from "@/components/main/CHighlightedText";
import CLabel from "@/components/main/CLabel";
import t from "@/lib/localization";
import {
  Alert,
  Button,
  Container,
  Grid,
  Input,
  createStyles,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import axios from "axios";

import React, { useState } from "react";
import { z } from "zod";
import { showToaster } from "@/Helpers/hooks/toaster";

const useStyles = createStyles((theme) => ({
  avatar: {
    height: "50px",
    width: "50px",
    borderRadius: "50%",
  },
  comName: {
    fontSize: "18px",
    lineHeight: "28px",
    color: "#777777",
  },
  para: {
    color: "#A4A4A4",
    fontSize: "14px",
    lineHeight: "24px",
  },
  newsLetter: {
    position: "relative",
    "&:before": {
      content: '""',
      position: "absolute",
      width: "222.6px",
      left: "40.5%",
      top: "-45px",
      height: "83px",
      backgroundImage: `url(/images/horizantalTexture.png)`,
      backgroundRepeat: "no-repeat",
      zIndex: 10,
    },
    "&:after": {
      content: '""',
      position: "absolute",
      right: "30px",
      width: "20%",
      transform: " translate(97%, -130%)",
      height: "222.6px",
      backgroundImage: `url(/images/verticalTexture.png)`,
      backgroundRepeat: "no-repeat",
      zIndex: 10,
      [theme.fn.smallerThan("md")]: {
        display: "none",
      },
    },
  },
}));

export default function Newsletter({
  title = "",
  description = "",
  badgeTitle = "",
  dictionary,
  children,
}) {
  const { classes } = useStyles();

  const schema = z.object({
    email: z.string().email({
      message: t(dictionary.pleaseEnterAValidItem, {
        name: dictionary.email,
      }),
    }),
  });

  const form = useForm({
    validateInputOnChange: true,
    validate: zodResolver(schema),
    initialValues: {
      email: "",
    },
  });

  const [loading, setLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const submit = async (values) => {
    try {
      setLoading(true);
      if (!executeRecaptcha) {
        setLoading(false);
        return;
      }
      const token = await executeRecaptcha();
      if (!token) {
        setLoading(false);
        return;
      }
      const res = await axios.post("/api/newsletter", {
        ...values,
        token: token,
      });
      setLoading(false);
      setSubmitSuccess(true);
    } catch (error) {
      showToaster(dictionary.unexpectedError, "red", IconAlertCircle);
      setLoading(false);
    }
  };

  return (
    <Container
      className={`my-[95px] bg-brandGrey overflow-hidden rounded-[34px] py-[50px] px-2 md:ps-2 mx-4 xl:mx-auto ${classes.newsLetter}`}>
      <Grid>
        <Grid.Col
          span={12}
          md={5}
          lg={5}
          className='flex items-center md:pb-0'
          orderLg={1}
          orderMd={1}
          orderSm={2}
          orderXs={2}>
          {children}
        </Grid.Col>
        <Grid.Col
          span={12}
          md={7}
          lg={7}
          orderLg={2}
          orderMd={2}
          orderSm={1}
          orderXs={1}
          className='lg:ps-[70px] flex flex-col justify-around mb-8 px-4'>
          <div>
            <CLabel text={badgeTitle} />
            <CHighlightedText text={title} />
            <p className='lg:max-w-[70%] mt-2 mb-3 text-textGrey'>
              {description}
            </p>
          </div>
          {submitSuccess ? (
            <Alert
              icon={<IconCheck />}
              title={dictionary.success}
              className='max-w-[500px]'>
              {dictionary.newsletterSubscriptionMessage}
            </Alert>
          ) : (
            <form onSubmit={form.onSubmit(submit)}>
              <Input
                variant='filled'
                placeholder='Your Email'
                styles={{
                  input: {
                    "::placeholder": {
                      fontSize: "16px",
                      color: "#A4A4A4",
                      fontWeight: "500px",
                    },
                  },
                  input: {
                    paddingRight: "135px",
                  },
                }}
                radius='md'
                className='search-input md:w-[75%]'
                size='xl'
                rightSection={
                  <Button
                    className='me-20 '
                    variant='gradient'
                    type='submit'
                    loading={loading}>
                    {dictionary.subscribe}
                  </Button>
                }
                {...form.getInputProps("email")}
              />
              <div
                className={`custom-error text-red-500 text-[14px] mt-1 ${
                  form.errors.email ? "show-c" : "hidden-c"
                } `}>
                <div className={`  overflow-hidden`}>{form.errors.email}</div>
              </div>
              <style jsx global>{`
								.custom-error.show-c div {
									height: 16px;
									transition: all 0.5s;
								}
								.custom-error.hidden-c div {
									height: 0px;
									transition: all 0.5s;
								}
							`}</style>
            </form>
          )}
        </Grid.Col>
      </Grid>
    </Container>
  );
}
